import { QueryClient, QueryFunction } from "@tanstack/react-query";

const ACCESS_TOKEN_KEY = "catho_paroisses_access_token";

export function getAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function setAccessToken(token: string | null) {
  if (typeof window === "undefined") return;
  if (token) {
    window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
  } else {
    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  }
}

function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers: Record<string, string> = {
    ...authHeaders(),
  };
  if (data) headers["Content-Type"] = "application/json";

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  await throwIfResNotOk(res);
  return res;
}

export interface UploadResult {
  url: string;
  contentType?: string;
  size?: number;
  filename?: string;
}

/**
 * Téléverse un fichier binaire en multipart/form-data (champ `file`).
 * On ne fixe PAS Content-Type : le navigateur génère le boundary multipart.
 */
export async function uploadFile(url: string, file: File): Promise<UploadResult> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(url, {
    method: "POST",
    headers: { ...authHeaders() },
    body: fd,
  });
  await throwIfResNotOk(res);
  return (await res.json()) as UploadResult;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      headers: authHeaders(),
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

// Rétablissement automatique après un redéploiement backend (constat PO 05/09/2026).
// Pendant un redéploiement, les requêtes échouent en réseau/5xx pendant ~2 min,
// mais le navigateur reste « online » → refetchOnReconnect (event navigator.onLine)
// ne se déclenche jamais. On garde donc la requête en RÉESSAI (elle reste en
// chargement au lieu de tomber en erreur figée) jusqu'au retour du backend.
// throwIfResNotOk produit `${status}: ${text}` ; une erreur réseau n'a pas de
// préfixe (status 0). Les 4xx (401/403/404/400/422…) sont définitifs → pas de
// réessai, l'erreur remonte vite (et la logique de logout 401 reste intacte).
function shouldRetryTransient(failureCount: number, error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  const match = /^(\d{3}):/.exec(message);
  const status = match ? Number(match[1]) : 0;
  if (status >= 400 && status < 500 && status !== 429) return false;
  return failureCount < 8; // ~2-3 min avec le backoff plafonné à 30 s
}
const transientRetryDelay = (attemptIndex: number): number =>
  Math.min(1000 * 2 ** attemptIndex, 30000);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      staleTime: Infinity,
      retry: shouldRetryTransient,
      retryDelay: transientRetryDelay,
    },
    mutations: {
      retry: false,
    },
  },
});
